import React from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Quote, Plus } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import useBadgeToast from "@/components/site/BadgeToast";
import PageBlocks from "@/components/site/PageBlocks";
import PageHeader from "@/components/site/PageHeader";
import RichTextEditor, { SafeHTML } from "@/components/site/RichTextEditor";
import ReportButton from "@/components/site/ReportButton";

const FMT_COLOR = { article: "bg-cef-blue text-white", video: "bg-cef-pink text-white", podcast: "bg-cef-yellow", anonyme: "bg-black text-white" };

export default function Temoignages() {
  const { user } = useAuth();
  const showBadges = useBadgeToast();
  const [items, setItems] = React.useState([]);
  const [showForm, setShowForm] = React.useState(false);
  const [form, setForm] = React.useState({ author: user?.name || "Anonyme", text: "", format: "anonyme", theme: "", age: user?.age || "", school: user?.school || "", website: "" });

  const load = () => api.get("/testimonials").then((r) => setItems(r.data));
  React.useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.text.trim()) return toast.error("Écris ton témoignage.");
    try {
      const { data } = await api.post("/testimonials", {
        ...form,
        age: form.age ? parseInt(form.age) : null,
        school: form.school || null,
        author: form.format === "anonyme" ? "Anonyme" : (form.author || "Anonyme"),
      });
      if (data.pending_moderation) {
        toast.success("Merci ! Ton témoignage anonyme va être validé par notre équipe avant publication.");
      } else {
        toast.success("Merci pour ton témoignage ✊");
      }
      if (data.new_badges?.length) showBadges(data.new_badges);
      setShowForm(false);
      setForm({ author: user?.name || "Anonyme", text: "", format: "anonyme", theme: "", age: user?.age || "", school: user?.school || "", website: "" });
      load();
    } catch {
      toast.error("Erreur, réessaye.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <PageHeader slug="temoignages" fallbackTitle="Témoignages" fallbackSubtitle="La parole directe des élèves. Vraies histoires, vrais visages, vraies voix." />
        </div>
        <button onClick={() => setShowForm((s) => !s)} className="neo-btn bg-cef-pink text-white" data-testid="add-testimonial-btn"><Plus className="w-4 h-4" /> Témoigner</button>
      </div>

      <PageBlocks slug="temoignages" className="mt-6" />

      {showForm && (
        <form onSubmit={submit} className="mt-6 neo-card p-5 grid sm:grid-cols-2 gap-3" data-testid="testimonial-form">
          <select className="neo-input" value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })} data-testid="t-format">
            <option value="anonyme">Anonyme</option>
            <option value="article">Sous mon prénom</option>
          </select>
          <input className="neo-input" placeholder="Thème (ex: harcèlement, bien-être...)" value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })} data-testid="t-theme" />
          {form.format !== "anonyme" && (
            <>
              <input className="neo-input" placeholder="Ton prénom" value={form.author === "Anonyme" ? "" : form.author} onChange={(e) => setForm({ ...form, author: e.target.value })} data-testid="t-author" />
              <input className="neo-input" placeholder="Ton école (optionnel)" value={form.school} onChange={(e) => setForm({ ...form, school: e.target.value })} data-testid="t-school" />
            </>
          )}
          <input className="neo-input" type="number" placeholder="Ton âge (optionnel)" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} data-testid="t-age" />
          <div className="sm:col-span-2">
            <div className="text-xs font-bold uppercase tracking-tight mb-1">Ton histoire</div>
            <RichTextEditor value={form.text} onChange={(html) => setForm({ ...form, text: html })} placeholder="Raconte ton histoire — gras, italique, photos, vidéo YouTube…" minHeight={180} maxHeight={420} testId="t-text-editor" />
          </div>
          {form.format === "anonyme" && <p className="text-xs text-neutral-500 sm:col-span-2">⏳ Les témoignages anonymes sont modérés avant publication.</p>}
          {/* Honeypot — invisible to humans */}
          <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
            value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
            className="hidden" />
          <button type="submit" className="neo-btn bg-cef-blue text-white sm:col-span-2" data-testid="t-submit">Publier</button>
        </form>
      )}

      <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="testimonials-grid">
        {items.map((t) => (
          <div key={t.id} className="neo-card p-6" data-testid={`testimonial-${t.id}`}>
            <Quote className="w-6 h-6 text-cef-pink" strokeWidth={2.5} />
            {(t.text || "").trim().startsWith("<")
              ? <SafeHTML html={t.text} className="mt-2 text-base leading-relaxed max-h-[260px] overflow-y-auto" />
              : <p className="mt-2 text-base leading-relaxed">« {t.text} »</p>}
            <div className="mt-4 flex items-center justify-between text-sm">
              <span className="font-bold">{t.author}{t.age ? `, ${t.age} ans` : ""}</span>
              <span className={`chip ${FMT_COLOR[t.format]}`}>{t.format}</span>
            </div>
            {t.theme && <div className="mt-2 text-xs text-neutral-500">#{t.theme}</div>}
            <div className="mt-3 pt-2 border-t border-black/10 flex justify-end">
              <ReportButton targetType="testimonial" targetId={t.id} testId={`report-testimonial-${t.id}`} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
